import type {
  SchedulerNode,
  ConversationNode,
  SchedulerDatum,
  EditableGraph,
  Id,
} from 'src/types/chatbot';
import type {
  AdditionalSchedulerQuestion,
  SchedulerAnswerType,
} from 'src/types/scheduler';
import type {Validation} from 'src/utils/validation';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector, useDispatch} from 'react-redux';
import lodashKeyBy from 'lodash/keyBy';

import * as api from 'src/utils/api-no-store';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {useEditorState, finalizeReducerState, useAtsVariables} from './reducer';
import {validateNode} from './validation';
import {filterValidation} from 'src/utils/validation';

import {
  useSchedulerTeamTemplates,
  useChatbotSchedulerTemplates,
} from 'src/hooks/api/scheduler';
import {fromArray, keyBy} from 'src/utils/map';
import {createRawDraftContentState, hasText} from 'src/utils/draft';

import {NodePopover} from './container';
import {FormPair, ButtonsSmall} from 'src/components/conversation-builder/form';
import {
  PadSection,
  Subsection,
  useFirstSmsMessage,
  Table,
  TableTitle,
  TableBody,
  TableRowPadded,
} from './common';
import {
  findAncestorNodeVariables,
  validationTypeByQuestionType,
  getQuestionText,
  getTemplateTypeOptions,
} from 'src/components/conversation-builder/utils';

import {getAllNamespacedGlobalVariables} from 'src/action-creators/namespaced-global-variables';

import VariablePickerSelect from 'src/components/lib/variable-picker/select-2';
import {TabGroup} from './tab-group';
import {MessageDraftEditor} from 'src/components/conversation-builder/draft-editor';
import {Select} from 'src/components/lib/token-list-input';
import {AllVariablePicker} from './variable-picker';
import {Toggle} from './node-link-toggle';
import Button from 'src/components/lib/new-button';
import Loading from 'src/components/lib/loading';

import css from './scheduler.css';


type VariableTemplateStatus = 'loading' | 'success' | 'error' | undefined;

export default function SchedulerEditor({
  defaultNode,
  graph,
  hasSubmitted,
  entityType,
  onSubmit,
  edgeId,
  ...props
}: {
  entityType: EntityType;
  onClose: () => unknown;
  onSubmit: (arg1: SchedulerNode) => unknown;
  defaultNode: SchedulerNode;
  graph: EditableGraph;
  actionName: 'add' | 'edit';
  hasSubmitted: boolean;
  readOnly: boolean;
  edgeId?: Id;
}) {
  const {t, makeKey} = useLabelTranslation();
  const schedulerNodeEnhancementEnabled = useReleaseFlag(
    'chatbotSchedulerNodeEnhancement',
  );
  const {readOnly, onClose, actionName} = props;

  const isSourcingScheduler = graph.use_case === 'sourcing';
  const isFirstSmsMessage = useFirstSmsMessage(graph, defaultNode);
  const templateTypeOptions = getTemplateTypeOptions(t);
  const [state, dispatch] = useEditorState({
    defaultNode,
  });

  // @ts-expect-error - TS7006 - Parameter 'payload' implicitly has an 'any' type.
  const update = (payload) => dispatch({type: 'update', payload});
  const {node} = state;

  const error = hasSubmitted ? validateNode(node, graph) : null;

  const nodes = React.useMemo(
    () => [...graph.nodes.values()].filter((node) => node.type === 'question'),
    [graph.nodes],
  );

  const templates = useChatbotSchedulerTemplates();
  const templatesMap = React.useMemo(() => fromArray(templates), [templates]);

  const messageError = filterValidation(error, ['text_draft_json']);
  const calendarError = filterValidation(error, [
    'scheduler_template_id',
    'scheduler_template_questions',
    'scheduler_template_additional_questions',
    'scheduler_mapping_var',
  ]);
  const detailsError = filterValidation(error, ['event_info']);

  const labels = useAtsVariables(entityType);

  const dynamicLabels = React.useMemo(
    () => [
      ...labels.filter(
        (label) =>
          label.mapping.is_internal === true &&
          label.entityRelationshipPath[0] === 'id',
      ),
    ],
    [labels],
  );
  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));
  const nodeVariables = findAncestorNodeVariables(graph, defaultNode, edgeId);

  React.useEffect(() => {
    if (node.scheduler_template_id !== defaultNode.scheduler_template_id) {
      update({
        scheduler_data: {
          ...node.scheduler_data,
          additional_questions:
            node.scheduler_template_id != null
              ? templatesMap
                  .get(node.scheduler_template_id)
                  ?.additional_questions.map(
                    (q: AdditionalSchedulerQuestion) => ({
                      dynamic_field: null,
                      node_id: null,
                      name: q.question,
                    }),
                  ) ?? []
              : [],
        },
      });
    }
  }, [node.scheduler_template_id]);

  const reduxDispatch = useDispatch();

  React.useEffect(() => {
    if (schedulerNodeEnhancementEnabled && !node.scheduler_data.type) {
      update({
        scheduler_data: {
          ...node.scheduler_data,
          type: 'static_template',
        },
      });
    }
  }, []);

  React.useEffect(() => {
    if (schedulerNodeEnhancementEnabled) {
      reduxDispatch(getAllNamespacedGlobalVariables());
    }
  }, []);

  const globalVariables =
    useSelector((state) =>
      state.namespacedGlobalVariables?.filter(
        // @ts-expect-error - TS7031 - Binding element 'anchor_entity_type' implicitly has an 'any' type.
        ({anchor_entity_type}) =>
          anchor_entity_type === graph.anchor_entity_type,
      ),
    ) ?? [];

  const mappedGloablVariables = globalVariables.map(
    // @ts-expect-error - TS7031 - Binding element 'fully_formed_name' implicitly has an 'any' type. | TS7031 - Binding element 'global_variable' implicitly has an 'any' type.
    ({fully_formed_name, global_variable}) => ({
      ...global_variable,
      value: global_variable.id,
      label: fully_formed_name,
    }),
  );
  const globalVariablesById = lodashKeyBy(mappedGloablVariables, 'id');

  const showQuestionsForStatic =
    node.scheduler_data.type === 'static_template' &&
    node.scheduler_template_id;
  const showQuestionsForDynamic =
    node.scheduler_data.type === 'dynamic_template' &&
    node.scheduler_data.global_variable;
  const showTemplateQuestions =
    !schedulerNodeEnhancementEnabled ||
    (schedulerNodeEnhancementEnabled &&
      (showQuestionsForStatic || showQuestionsForDynamic));

  const [variableTemplateStatus, setVariableTemplateStatus] =
    React.useState<VariableTemplateStatus>();

  const handleSelectGloablVariable = (selected: any | null | undefined) => {
    if (selected) {
      setVariableTemplateStatus('loading');

      api
        .get(`scheduler/event-template/${selected.value}/additional-questions`)
        .then((res) => {
          update({
            scheduler_data: {
              ...node.scheduler_data,
              // @ts-expect-error - TS7031 - Binding element 'template_ids' implicitly has an 'any' type. | TS7031 - Binding element 'question' implicitly has an 'any' type.
              additional_questions: res.map(({template_ids, question}) => ({
                node_id: null,
                dynamic_field: null,
                template_ids,
                name: question,
              })),
              global_variable: {
                id: selected.value,
                name: selected.label,
              },
            },
          });
          setVariableTemplateStatus('success');
        })
        .catch((err) => {
          update({
            scheduler_data: {
              ...node.scheduler_data,
              additional_questions: [],
              global_variable: null,
            },
          });
          setVariableTemplateStatus('error');
        });
    }
  };
  const tabs = [
    {
      id: 'message',
      title: t('Message', 'Message'),
      error: messageError,
      content: (
        <PadSection>
          <MessageDraftEditor
            label={`${t('Message', 'Message')}*`}
            placeholder={`${t(
              'I\'d_like_to_schedule_a_meeting_with_you_Select_times_that_work_for_you',
              'I\'d like to schedule a meeting with you. Select times that work for you',
            )}.`}
            defaultValue={node.text_draft_json}
            onChange={(text_draft_json) => update({text_draft_json})}
            entityType={entityType}
            isFirstSmsMessage={isFirstSmsMessage}
            error={error?.text_draft_json}
            readOnly={Boolean(readOnly)}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
          />
        </PadSection>
      ),
    },
    {
      id: 'calendar',
      title: t('Calendar', 'Calendar'),
      error: calendarError,
      content: (
        <>
          <PadSection>
            <Subsection>
              <p>
                {`${t('Select_a_team', 'Select a team')}${
                  schedulerNodeEnhancementEnabled
                    ? ` ${t('or_dynamic', 'or dynamic')}`
                    : ''
                } ${t(
                  'template_for_this_meeting_scheduler_You_cannot_use_personal_templates_for_this_node_Then_map_the_template_questions_to_a_node_value_or_a_variable',
                  'template for this meeting scheduler. You cannot use personal templates for this node. Then, map the template questions to a node value or a variable',
                )}.`}
              </p>
              {schedulerNodeEnhancementEnabled && (
                <FormPair label={t('Select_one', 'Select one')} TagName="div">
                  <Select
                    disabled={readOnly}
                    value={templateTypeOptions.find(
                      ({value}) => value === node.scheduler_data.type,
                    )}
                    options={templateTypeOptions}
                    onChange={(selected) => {
                      if (selected) {
                        // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
                        setVariableTemplateStatus();
                        update({
                          scheduler_data: {
                            ...node.scheduler_data,
                            type: selected.value,
                            global_variable: null,
                            additional_questions: [],
                          },
                        });
                      }
                    }}
                    hasLabel
                    resolveKey={(option) => option.value}
                    components={{
                      ValueComponent: () => (
                        <div>
                          {
                            templateTypeOptions.find(
                              ({value}) => value === node.scheduler_data.type,
                            )?.label
                          }
                        </div>
                      ),
                    }}
                    resolveLabel={(option) => (
                      <div className={css.column}>
                        <div>{t(makeKey(option.label), option.label)}</div>
                        <div>
                          {t(makeKey(option.description), option.description)}
                        </div>
                      </div>
                    )}
                    error={error?.scheduler_template_id}
                  />
                </FormPair>
              )}

              {((schedulerNodeEnhancementEnabled &&
                node.scheduler_data.type === 'static_template') ||
                !schedulerNodeEnhancementEnabled) && (
                <FormPair
                  label={t('Select_a_team_template', 'Select a team template')}
                  TagName="div"
                >
                  <Select
                    searchable
                    clearable
                    disabled={readOnly}
                    value={
                      node.scheduler_template_id
                        ? // this .get lookup will fail if the user does not have permission
                          // to access multi-event-templates which are set by 'admin' users
                          // as defined by scheduler: i.e. users that can edit 'settings'
                          templatesMap.get(node.scheduler_template_id) ?? {
                            id: node.scheduler_template_id ?? '',
                            name: node.scheduler_template_id
                              ? '[Template only visible to administrators]'
                              : '',
                          }
                        : null
                    }
                    options={templates}
                    onChange={(template) =>
                      update({scheduler_template_id: template?.id})
                    }
                    resolveLabel={(template) => template.name}
                    resolveKey={(template) => template.id}
                    error={error?.scheduler_template_id}
                  />
                </FormPair>
              )}

              {schedulerNodeEnhancementEnabled &&
                node.scheduler_data.type === 'dynamic_template' && (
                  //Choose global variables
                  <FormPair
                    label={`${t(
                      'Select_template_mapping_variable',
                      'Select template mapping variable',
                    )}:`}
                    TagName="div"
                  >
                    <Select
                      searchable
                      disabled={readOnly}
                      value={
                        //this could be nullish, need to check after types are in a good place
                        node.scheduler_data.global_variable?.id
                          ? globalVariablesById[
                              node.scheduler_data.global_variable.id
                            ]
                          : null
                      }
                      options={mappedGloablVariables}
                      onChange={handleSelectGloablVariable}
                      resolveLabel={(template) => template.name}
                      resolveKey={(template) => template.id}
                      error={error?.scheduler_mapping_var}
                    />
                    {variableTemplateStatus === 'error' && (
                      <div className={css.errorText}>{`${t(
                        'Global_variable_does_not_have_any_template_mappings',
                        'Global variable does not have any template mappings',
                      )}.`}</div>
                    )}
                  </FormPair>
                )}
            </Subsection>
          </PadSection>

          {variableTemplateStatus === 'loading' && (
            <div className={css.loading}>
              <Loading />
              {t('Loading_questions', 'Loading questions')}
            </div>
          )}

          {showTemplateQuestions && (
            <Table>
              <TableTitle>
                {t('Map_template_questions', 'Map template questions')}
              </TableTitle>
              <TableBody>
                {node.scheduler_data.questions.map((question, index) => (
                  <QuestionPicker
                    key={question.name}
                    question={question}
                    nodes={nodes}
                    entityType={entityType}
                    readOnly={readOnly}
                    onChange={(nextQuestion) => {
                      update({
                        scheduler_data: {
                          ...node.scheduler_data,
                          questions: node.scheduler_data.questions.map(
                            (currentQuestion) =>
                              question === currentQuestion
                                ? nextQuestion
                                : currentQuestion,
                          ),
                        },
                      });
                    }}
                    error={error?.scheduler_template_questions?.[String(index)]}
                    type={question.name}
                    variableType={isSourcingScheduler ? 'node' : 'all'}
                  />
                ))}
              </TableBody>
            </Table>
          )}

          {node.scheduler_data.additional_questions.length > 0 && (
            <Table>
              <TableTitle>
                {t('Map_additional_questions', 'Map additional questions')}
              </TableTitle>
              <TableBody>
                {node.scheduler_data.additional_questions.map(
                  (question, index) => (
                    <QuestionPicker
                      key={question.name}
                      question={question}
                      nodes={nodes}
                      entityType={entityType}
                      readOnly={readOnly}
                      onChange={(nextQuestion) => {
                        update({
                          scheduler_data: {
                            ...node.scheduler_data,
                            additional_questions:
                              node.scheduler_data.additional_questions.map(
                                (currentQuestion) =>
                                  question === currentQuestion
                                    ? nextQuestion
                                    : currentQuestion,
                              ),
                          },
                        });
                      }}
                      type={
                        node.scheduler_template_id
                          ? keyBy(
                              templatesMap.get(node.scheduler_template_id)
                                ?.additional_questions ?? [],
                              'question',
                            ).get(question.name)?.answer_type
                          : undefined
                      }
                      error={
                        error?.scheduler_template_additional_questions?.[
                          String(index)
                        ]
                      }
                      variableType={isSourcingScheduler ? 'node' : 'all'}
                    />
                  ),
                )}
              </TableBody>
            </Table>
          )}
        </>
      ),
    },
  ];

  if (schedulerNodeEnhancementEnabled) {
    tabs.push({
      id: 'details',
      title: t('Meeting_Details', 'Meeting Details'),
      error: detailsError,
      content: (
        <>
          <PadSection>
            <Subsection>
              <p>{`${t(
                'Meeting_details_will_display_on_the_calendar_invite',
                'Meeting details will display on the calendar invite',
              )}.`}</p>
            </Subsection>
          </PadSection>
          <PadSection>
            <MessageDraftEditor
              label={t('Meeting_title', 'Meeting title')}
              placeholder={`${t(
                '1_hr_Meeting_with',
                '1 hr Meeting with',
              )} <recruiter.name>`}
              defaultValue={
                node.scheduler_data.event_info?.event_title ??
                createRawDraftContentState()
              }
              onChange={(text_draft_json) =>
                update({
                  scheduler_data: {
                    ...node.scheduler_data,
                    event_info: {
                      event_description:
                        node.scheduler_data.event_info?.event_description,
                      event_title: text_draft_json,
                    },
                  },
                })
              }
              entityType={entityType}
              isFirstSmsMessage={isFirstSmsMessage}
              error={
                error?.event_info &&
                (!node.scheduler_data.event_info?.event_title ||
                  (node.scheduler_data.event_info.event_title &&
                    !hasText(node.scheduler_data.event_info.event_title)))
                  ? error.event_info
                  : undefined
              }
              readOnly={Boolean(readOnly)}
              useCase={graph.use_case}
              nodeVariables={nodeVariables}
            />
          </PadSection>

          <PadSection>
            <MessageDraftEditor
              label={t('Meeting_details', 'Meeting details')}
              placeholder={`${t(
                'Guest_notes',
                'Guest notes',
              )}\n<candidate.name>\n${t(
                'Email',
                'Email',
              )}: <candidate.email>\n${t(
                'Phone',
                'Phone',
              )}:  <candidate.phone>`}
              defaultValue={
                node.scheduler_data.event_info?.event_description ??
                createRawDraftContentState()
              }
              onChange={(text_draft_json) =>
                update({
                  scheduler_data: {
                    ...node.scheduler_data,
                    event_info: {
                      event_title: node.scheduler_data.event_info?.event_title,
                      event_description: text_draft_json,
                    },
                  },
                })
              }
              entityType={entityType}
              isFirstSmsMessage={isFirstSmsMessage}
              error={
                error?.event_info &&
                (!node.scheduler_data.event_info?.event_description ||
                  (node.scheduler_data.event_info.event_description &&
                    !hasText(node.scheduler_data.event_info.event_description)))
                  ? error.event_info
                  : undefined
              }
              readOnly={Boolean(readOnly)}
              useCase={graph.use_case}
              nodeVariables={nodeVariables}
            />
          </PadSection>
        </>
      ),
    });
  }

  if (!isSourcingScheduler) {
    // @ts-expect-error - TS2345 - Argument of type '{ id: string; title: string; content: React.JSX.Element; }' is not assignable to parameter of type '{ id: string; title: string; error: Validation | null | undefined; content: React.JSX.Element; }'.
    tabs.push({
      id: 'hosts',
      title: t('Hosts', 'Hosts'),
      content: (
        <PadSection>
          <Subsection>
            <p>{`${t(
              'This_step_is_optional_The_primary_host_will_be_prioritized_to_be_scheduled_for_meetings_over_secondary_host_and_other_team_members_in_the_selected_template',
              'This step is optional. The primary host will be prioritized to be scheduled for meetings, over secondary host and other team members in the selected template',
            )}.`}</p>
            <FormPair
              label={t(
                'Primary_meeting_host_optional',
                'Primary meeting host (optional)',
              )}
            >
              {/* @ts-expect-error - TS2739 - Type '{ baseEntityType: EntityType; searchable: true; autofocus: true; placeholder: string; limit: number; values: any[]; options: any[]; onChange: (options: any[]) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<any>': resolveKey, resolveLabel */}
              <VariablePickerSelect
                baseEntityType={entityType}
                searchable
                autofocus
                //baseClassNames={borderlessClassNames}
                placeholder={t('Search_variables', 'Search variables')}
                limit={1}
                values={[
                  dynamicLabelMap.get(
                    node.scheduler_data.contacts.find(
                      (d) => d.name === 'primary',
                    )?.dynamic_field,
                  ),
                ].filter(Boolean)}
                options={dynamicLabels}
                onChange={(options) => {
                  const fieldValue = options[0]?.value;
                  const stub = {
                    dynamic_field: fieldValue,
                    name: 'primary',
                    node_id: null,
                  } as const;
                  const contactList = node.scheduler_data.contacts.filter(
                    (c) => c.name !== 'primary',
                  );

                  update({
                    scheduler_data: {
                      ...node.scheduler_data,
                      contacts:
                        options.length === 0
                          ? contactList
                          : [...contactList, stub],
                    },
                  });
                }}
                disabled={readOnly}
              />
            </FormPair>
          </Subsection>

          <FormPair
            label={t(
              'Secondary_meeting_host_optional',
              'Secondary meeting host (optional)',
            )}
          >
            {/* @ts-expect-error - TS2739 - Type '{ baseEntityType: EntityType; searchable: true; placeholder: string; limit: number; values: any[]; options: any[]; onChange: (options: any[]) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<any>': resolveKey, resolveLabel */}
            <VariablePickerSelect
              baseEntityType={entityType}
              searchable
              placeholder={t('Search_variables', 'Search variables')}
              limit={1}
              values={[
                dynamicLabelMap.get(
                  node.scheduler_data.contacts.find(
                    (d) => d.name === 'secondary',
                  )?.dynamic_field,
                ),
              ].filter(Boolean)}
              options={dynamicLabels}
              onChange={(options) => {
                const fieldValue = options[0]?.value;
                const stub = {
                  dynamic_field: fieldValue,
                  name: 'secondary',
                  node_id: null,
                } as const;
                const contactList = node.scheduler_data.contacts.filter(
                  (c) => c.name !== 'secondary',
                );

                update({
                  scheduler_data: {
                    ...node.scheduler_data,
                    contacts:
                      options.length === 0
                        ? contactList
                        : [...contactList, stub],
                  },
                });
              }}
              disabled={readOnly}
            />
          </FormPair>
        </PadSection>
      ),
    });
  }

  const [currentTab, setCurrentTab] = React.useState('message');

  const tabIndex = tabs.findIndex((tab) => tab.id === currentTab);
  const isLastTab = tabIndex === tabs.length - 1;

  const handleSubmit = () => {
    if (actionName === 'add' && !isLastTab) {
      setCurrentTab(tabs[tabIndex + 1].id);
    } else {
      onSubmit(finalizeReducerState(state));
    }
  };

  // NOTE (kyle): try to stear the user to the tab that shows the error.
  const hasError = Boolean(error);
  React.useEffect(() => {
    if (hasError && hasSubmitted) {
      if (error?.text_draft_json) {
        setCurrentTab('message');
      } else if (error?.scheduler_template_id) {
        setCurrentTab('calendar');
      } else if (error?.event_info) {
        setCurrentTab('details');
      }
    }
  }, [hasError, hasSubmitted]);

  return (
    <NodePopover
      onChangeName={(name) => update({name})}
      state={state}
      error={error}
      {...props}
    >
      <TabGroup currentTab={currentTab} onChange={setCurrentTab} tabs={tabs}>
        <PadSection>
          <ButtonsSmall>
            <Button size="medium" onClick={onClose}>
              {readOnly ? t('Done', 'Done') : t('Cancel', 'Cancel')}
            </Button>
            {!readOnly && (
              <Button
                type="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={error}
              >
                {actionName === 'edit'
                  ? t('Done', 'Done')
                  : isLastTab
                  ? t('Add', 'Add')
                  : t('Next', 'Next')}
              </Button>
            )}
          </ButtonsSmall>
        </PadSection>
      </TabGroup>
    </NodePopover>
  );
}

function QuestionPicker({
  question,
  nodes,
  onChange,
  readOnly,
  entityType,
  error,
  type,
  variableType = 'all',
}: {
  question: SchedulerDatum;
  nodes: ConversationNode[];
  onChange: (question: SchedulerDatum) => void;
  readOnly: boolean;
  entityType: EntityType;
  error?: Validation | null | undefined;
  type?: SchedulerAnswerType | string;
  variableType?: 'all' | 'ats' | 'node';
}) {
  const {t} = useLabelTranslation();
  const [valueType, setValueType] = React.useState<'ats' | 'node'>(
    variableType !== 'all' ? variableType : question.node_id ? 'node' : 'ats',
  );

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const inputRef = React.useRef();
  const handleButtonClick = (type: string) => {
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<"node" | "ats">'.
    setValueType(type);
    setTimeout(() => {
      // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
      inputRef.current?.focus();
    }, 0);
  };

  const nodeOptions = type
    ? nodes.filter((node) => {
        // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'.
        if (node.answers || type === 'dropdown') {
          // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'.
          const isDropdownNode = node.answers && type === 'dropdown';
          const isBooleanNode =
            // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'ConversationNode'.
            node.response_type &&
            // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'ConversationNode'.
            node.response_type === 'boolean' &&
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly phone: "phone number"; readonly email: "email"; readonly name: "string"; readonly number: "number"; readonly checkbox: "boolean"; readonly consent: "boolean"; readonly multi_line_text: "string"; readonly dropdown: "string"; }'.
            validationTypeByQuestionType[type] === 'boolean';

          return isDropdownNode || isBooleanNode;
          // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'ConversationNode'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly phone: "phone number"; readonly email: "email"; readonly name: "string"; readonly number: "number"; readonly checkbox: "boolean"; readonly consent: "boolean"; readonly multi_line_text: "string"; readonly dropdown: "string"; }'.
        } else if (node.response_type && validationTypeByQuestionType[type]) {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly phone: "phone number"; readonly email: "email"; readonly name: "string"; readonly number: "number"; readonly checkbox: "boolean"; readonly consent: "boolean"; readonly multi_line_text: "string"; readonly dropdown: "string"; }'. | TS2339 - Property 'response_type' does not exist on type 'ConversationNode'.
          return validationTypeByQuestionType[type] === node.response_type;
        } else {
          //no validation requirement, default to true
          return true;
        }
      })
    : nodes;

  const questionText = getQuestionText(t);

  return (
    <TableRowPadded key={question.name}>
      {valueType === 'node' ? (
        <Select
          classNames={{container: css.columnInput}}
          options={nodeOptions}
          value={nodes.find((node) => node.id === question.node_id)}
          resolveLabel={(node) => node.name}
          resolveKey={(node) => String(node.id)}
          onChange={(node) =>
            onChange({
              ...question,
              node_id: node?.id,
              dynamic_field: null,
            })
          }
          clearable
          disabled={readOnly}
          placeholder={
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ name: string; email: string; purpose: string; phone: string; }'.
            question.node_id ? '' : questionText[question.name] ?? question.name
          }
          hasLabel
          inputProps={{
            // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLInputElement> | undefined'.
            ref: inputRef,
            autoComplete: 'sense-node',
          }}
          error={error}
        />
      ) : (
        <AllVariablePicker
          className={css.columnInput}
          entityType={entityType}
          type={type}
          value={question.dynamic_field}
          onChange={(attribute) => {
            onChange({
              ...question,
              dynamic_field: attribute?.value,
              node_id: null,
            });
          }}
          readOnly={readOnly}
          placeholder={
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ name: string; email: string; purpose: string; phone: string; }'.
            question.node_id ? '' : questionText[question.name] ?? question.name
          }
          hasLabel
          inputProps={{
            // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLInputElement> | undefined'.
            ref: inputRef,
            autoComplete: 'sense-entity-attribute',
          }}
          error={error}
        />
      )}
      {!readOnly && variableType === 'all' && (
        <Toggle
          value={valueType === 'ats'}
          onChange={(isVariable) =>
            handleButtonClick(isVariable ? 'ats' : 'node')
          }
        />
      )}
    </TableRowPadded>
  );
}
